import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getAccDashboard, resolveException, dismissException } from '../api';
import { accDashboardKeys } from './queryKeys';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

/**
 * Fetch the bookkeeper dashboard payload.
 * staleTime 5 min — data is computed server-side and rarely changes mid-session.
 */
export function useAccDashboard() {
  return useQuery({
    queryKey: accDashboardKeys.dashboard(),
    queryFn: getAccDashboard,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Resolve an acc_exception and invalidate the dashboard so counts update.
 */
export function useResolveException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      resolveException(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accDashboardKeys.dashboard() });
      toast.success('Exception resolved');
    },
    onError: (error) => {
      toast.error('Failed to resolve exception', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Dismiss an acc_exception and invalidate the dashboard.
 * Callers must show a confirmation before invoking.
 */
export function useDismissException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      dismissException(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accDashboardKeys.dashboard() });
      toast.success('Exception dismissed');
    },
    onError: (error) => {
      toast.error('Failed to dismiss exception', { description: getErrorMessage(error) });
    },
  });
}
